/** The Settings panel and direct-link page share one connector list. */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import type {
  ConnectorListEntry,
  ConnectorListResponse,
} from "@tabbio/types";
import { useLocale } from "../../hooks/useLocale";
import { useUpgradeModal } from "../../providers/UpgradeModalProvider";
import { useEntitlement } from "../../providers/SubscriptionProvider.shared";
import { createUpgradeContext } from "../../lib/monetization-context";
import { WebPageHeader } from "../common/WebPageHeader";
import { MaterialSymbol } from "../icons/MaterialSymbol";
import { BRAND_SVG } from "./brand-logos";
import { ConnectorSetupDialog } from "./ConnectorSetupDialog.web";
import { confirm } from "../../lib/alert.web";
import {
  connectConnector,
  createConnectSession,
  isConnectorEntitlementDenied,
  listConnectors,
  revokeConnection,
} from "../../lib/connectors-api";
import { openNangoConnect } from "../../lib/nango-connect.web";
import { usePublishPageContext } from "../../lib/page-context";
import { buildConnectorsPageContextState } from "../../lib/connectors-page-context";

import { isActive, isComingSoon, TOOLS_COMING_SOON } from './connector-availability';

function ConnectorRow({
  entry,
  busy,
  disabled,
  onAction,
}: {
  entry: ConnectorListEntry;
  busy?: boolean;
  disabled?: boolean;
  onAction?: () => void;
}) {
  const { t } = useTranslation("common");
  const { connector, connection } = entry;
  const connected = isActive(connection);
  const brand = BRAND_SVG[connector.slug];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "16px 0",
        borderBottom: "1px solid var(--input-border)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 36,
          height: 36,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
        }}
      >
        {brand ? (
          <img
            src={`data:image/svg+xml;utf8,${encodeURIComponent(brand)}`}
            width={32}
            height={32}
            alt=""
          />
        ) : (
          <MaterialSymbol name="extension" size={28} />
        )}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          dir="auto"
          style={{ fontSize: 15, fontWeight: 500, overflowWrap: "anywhere" }}
        >
          {connector.name}
        </div>
        <div
          dir="auto"
          style={{
            color: "var(--text-secondary)",
            fontSize: 13,
            lineHeight: 1.5,
            marginTop: 3,
          }}
        >
          {connected
            ? t("connectors.status.connected")
            : connector.rail === "ai"
              ? t("connectors.simple.aiDescription", { name: connector.name })
              : connector.description}
        </div>
      </div>
      {onAction ? (
        <button
          type="button"
          data-connector={connector.slug}
          onClick={onAction}
          disabled={disabled}
          aria-label={t(
            connected
              ? "connectors.simple.disconnectName"
              : "connectors.connectName",
            { name: connector.name },
          )}
          style={{
            ...actionStyle,
            ...(connected ? secondaryStyle : {}),
            flexShrink: 0,
            opacity: disabled ? 0.4 : 1,
            cursor: disabled ? "default" : "pointer",
          }}
        >
          {t(
            connected
              ? busy
                ? "connectors.revoking"
                : "connectors.revoke"
              : busy
                ? "connectors.connecting"
                : "connectors.connect",
          )}
        </button>
      ) : (
        <span
          style={{
            fontSize: 12,
            color: "var(--text-secondary)",
            flexShrink: 0,
          }}
        >
          {t("connectors.soon")}
        </span>
      )}
    </div>
  );
}

// Never mount this publisher inside Settings: its route is the page underneath
// the dialog, whose CV/job context must remain owned by that page.
function ConnectorsContext({ data }: { data: ConnectorListResponse }) {
  usePublishPageContext({
    screen: "connectors",
    state: buildConnectorsPageContextState(data, TOOLS_COMING_SOON),
  });
  return null;
}

function ConnectorsBody({ embedded }: { embedded: boolean }) {
  const { t } = useTranslation("common");
  const upgradeModal = useUpgradeModal();
  const [data, setData] = useState<ConnectorListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState(false);
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [setupTarget, setSetupTarget] = useState<
    ConnectorListEntry["connector"] | null
  >(null);
  const listRoot = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<string | null>(null);
  useEffect(() => {
    if (setupTarget || !returnFocus.current) return;
    const button = Array.from(
      listRoot.current?.querySelectorAll<HTMLButtonElement>(
        "button[data-connector]",
      ) ?? [],
    ).find((item) => item.dataset.connector === returnFocus.current);
    button?.focus();
    returnFocus.current = null;
  }, [setupTarget]);
  const pending = useRef(false);
  const alive = useRef(true);
  const request = useRef(0);
  const load = useCallback(async () => {
    const id = ++request.current;
    setLoading(true);
    setLoadError(false);
    try {
      const result = await listConnectors();
      if (alive.current && id === request.current) setData(result);
    } catch {
      if (alive.current && id === request.current) setLoadError(true);
    } finally {
      if (alive.current && id === request.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
      // A generation counter, not a DOM ref; invalidate any in-flight read.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      request.current++;
    };
  }, [load]);

  const handleConnectTool = async (entry: ConnectorListEntry) => {
    if (
      pending.current ||
      isComingSoon(entry.connector) ||
      isActive(entry.connection)
    )
      return;
    const caps = data?.caps;
    if (
      caps?.connectedToolsMax != null &&
      caps.connectedToolsUsed >= caps.connectedToolsMax
    ) {
      upgradeModal.open({
        placement: "connectors",
        entryPoint: "connectors.web_cap",
        planSet: "individual",
        recommendedTier: "plus",
        featureKey: "individual.connectors",
        reason: "wrong_tier",
        returnTo: "/connectors",
      });
      return;
    }
    pending.current = true;
    setBusySlug(entry.connector.slug);
    setActionError(false);
    try {
      if (entry.connector.authKind === "oauth") {
        const session = await createConnectSession(entry.connector.slug);
        await openNangoConnect({
          sessionToken: session.sessionToken,
          onResult: async ({ connectionId }) => {
            try {
              await connectConnector(entry.connector.slug, {
                nangoConnectionId: connectionId,
              });
            } catch {
              /* The webhook can persist the connection; refetch its state. */
            }
            if (alive.current) await load();
          },
          onClose: () => {
            if (alive.current) void load();
          },
        });
      } else {
        await connectConnector(entry.connector.slug);
        if (alive.current) await load();
      }
    } catch (error) {
      if (alive.current) {
        if (isConnectorEntitlementDenied(error) && error.decision) {
          upgradeModal.open(
            createUpgradeContext({
              decision: error.decision,
              placement: "connectors",
              entryPoint: "connectors.web_connect",
              returnTo: "/connectors",
            }),
          );
        } else setActionError(true);
      }
    } finally {
      pending.current = false;
      if (alive.current) setBusySlug(null);
    }
  };

  const handleDisconnect = async (entry: ConnectorListEntry) => {
    if (pending.current || !entry.connection) return;
    pending.current = true;
    setBusySlug(entry.connector.slug);
    setActionError(false);
    try {
      const ok = await confirm({
        title: t("connectors.revokeTitle", { name: entry.connector.name }),
        message: t("connectors.revokeMessage"),
        confirmLabel: t("connectors.revoke"),
        cancelLabel: t("actions.cancel"),
        destructive: true,
      });
      if (!ok || !alive.current) return;
      await revokeConnection(entry.connection.id);
      if (alive.current) await load();
    } catch {
      if (alive.current) setActionError(true);
    } finally {
      pending.current = false;
      if (alive.current) setBusySlug(null);
    }
  };

  const entries = [...(data?.rails.ai ?? []), ...(data?.rails.tool ?? [])];
  const connected = entries.filter((entry) => isActive(entry.connection));
  const available = entries.filter(
    (entry) => !isActive(entry.connection) && !isComingSoon(entry.connector),
  );
  const unavailable = entries.filter(
    (entry) => !isActive(entry.connection) && isComingSoon(entry.connector),
  );

  return (
    <div ref={listRoot}>
      {!embedded && data ? <ConnectorsContext data={data} /> : null}
      {setupTarget ? (
        <ConnectorSetupDialog
          key={setupTarget.slug}
          connector={setupTarget}
          embedded
          onClose={() => setSetupTarget(null)}
        />
      ) : (
        <>
          <p
            style={{
              margin: "0 0 16px",
              color: "var(--text-secondary)",
              fontSize: 14,
              lineHeight: 1.6,
            }}
          >
            {t("connectors.subtitle")}
          </p>
          {actionError ? (
            <p
              role="alert"
              style={{ fontSize: 14, color: "var(--text-secondary)" }}
            >
              {t("connectors.simple.actionFailed")}
            </p>
          ) : null}
          {loading ? (
            <p role="status" style={stateStyle}>
              {t("connectors.loading")}
            </p>
          ) : loadError ? (
            <div role="alert" style={stateStyle}>
              <p>{t("connectors.loadFailed")}</p>
              <button
                type="button"
                style={actionStyle}
                onClick={() => void load()}
              >
                {t("actions.retry")}
              </button>
            </div>
          ) : (
            <>
              {connected.length ? (
                <section aria-label={t("connectors.sections.connected")}>
                  <h3 style={sectionStyle}>
                    {t("connectors.sections.connected")}
                  </h3>
                  {connected.map((entry) => (
                    <ConnectorRow
                      key={entry.connector.slug}
                      entry={entry}
                      disabled={busySlug !== null}
                      busy={busySlug === entry.connector.slug}
                      onAction={() => void handleDisconnect(entry)}
                    />
                  ))}
                </section>
              ) : null}
              {available.length ? (
                <section aria-label={t("connectors.simple.available")}>
                  {connected.length ? (
                    <h3 style={sectionStyle}>
                      {t("connectors.simple.available")}
                    </h3>
                  ) : null}
                  {available.map((entry) => (
                    <ConnectorRow
                      key={entry.connector.slug}
                      entry={entry}
                      disabled={busySlug !== null}
                      busy={busySlug === entry.connector.slug}
                      onAction={() => {
                        if (entry.connector.rail === "ai") {
                          returnFocus.current = entry.connector.slug;
                          setSetupTarget(entry.connector);
                        } else {
                          void handleConnectTool(entry);
                        }
                      }}
                    />
                  ))}
                </section>
              ) : null}
              {unavailable.length ? (
                <details style={{ marginTop: 24 }}>
                  <summary
                    style={{
                      cursor: "pointer",
                      fontSize: 14,
                      color: "var(--text-secondary)",
                      minHeight: 44,
                      paddingBlock: 12,
                    }}
                  >
                    {t("connectors.status.comingSoon")} ({unavailable.length})
                  </summary>
                  {unavailable.map((entry) => (
                    <ConnectorRow key={entry.connector.slug} entry={entry} />
                  ))}
                </details>
              ) : null}
              {!entries.length ? (
                <p style={stateStyle}>{t("connectors.empty.title")}</p>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}

export function ConnectorsScreen({
  embedded = false,
  showHeading = true,
}: {
  embedded?: boolean;
  showHeading?: boolean;
  setupRoute?: string;
}) {
  const { t } = useTranslation("common");
  const { isRTL } = useLocale();
  const router = useRouter();
  const decision = useEntitlement("individual.connectors");
  const upgradeModal = useUpgradeModal();
  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className={embedded ? undefined : "h-full overflow-y-auto"}
      style={{
        color: "var(--text-primary)",
        minWidth: 0,
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      <div className={embedded ? undefined : "tabbio-svc-page"}>
        {embedded ? (
          showHeading ? (
            <h2 style={{ margin: "0 0 12px", fontSize: 24, fontWeight: 400 }}>
              {t("connectors.title")}
            </h2>
          ) : null
        ) : (
          <WebPageHeader
            title={t("connectors.title")}
            onBack={() =>
              router.canGoBack() ? router.back() : router.replace("/settings")
            }
            hideBackOnMobile
          />
        )}
        {decision.reasonCode === "loading" ? (
          <p role="status" style={stateStyle}>
            {t("entitlementGate.loading")}
          </p>
        ) : decision.allowed ? (
          <ConnectorsBody embedded={embedded} />
        ) : (
          <div style={{ paddingBlock: 16 }}>
            <p
              style={{
                color: "var(--text-secondary)",
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {t("connectors.subtitle")}
            </p>
            <button
              type="button"
              style={actionStyle}
              onClick={() =>
                upgradeModal.open(
                  createUpgradeContext({
                    decision,
                    placement: "connectors",
                    entryPoint: "entitlement_gate.individual.connectors",
                    returnTo: "/connectors",
                  }),
                )
              }
            >
              {t(
                decision.recommendedPlanSet === "employer"
                  ? decision.recommendedTier === "pro"
                    ? "entitlementGate.ctaUpgradeEmployerPro"
                    : "entitlementGate.ctaUpgradeEmployerPlus"
                  : decision.recommendedTier === "pro"
                    ? "entitlementGate.ctaUpgradeIndividualPro"
                    : "entitlementGate.ctaUpgradeIndividualPlus",
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
const actionStyle: CSSProperties = {
  minHeight: 40,
  padding: "8px 16px",
  borderRadius: 9999,
  border: "1px solid transparent",
  background: "var(--text-primary)",
  color: "var(--bg)",
  font: "inherit",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};
const secondaryStyle: CSSProperties = {
  borderColor: "var(--input-border)",
  background: "transparent",
  color: "var(--text-primary)",
};
const sectionStyle: CSSProperties = {
  margin: "24px 0 0",
  fontSize: 14,
  fontWeight: 500,
  color: "var(--text-secondary)",
};
const stateStyle: CSSProperties = {
  paddingBlock: 28,
  fontSize: 14,
  color: "var(--text-secondary)",
};
